import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showMobileLangMenu, setShowMobileLangMenu] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setShowLangMenu(false);
    setShowMobileLangMenu(false);
  };

  const getLanguageLabel = () => {
    switch (i18n.language) {
      case 'zh-TW':
        return '繁體中文';
      case 'ja':
        return '日本語';
      default:
        return 'English';
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-slate-950/95 backdrop-blur-lg border-b border-slate-800/50 shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-800 border border-slate-700">
              <img 
                src="https://static.readdy.ai/image/584ac40951726429431482b4d17e4edf/ff958525ef19bccc41cd8ac8f6d5e333.png" 
                alt="Scrollmate" 
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-white font-semibold text-lg font-['Space_Grotesk']">
              Scrollmate
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('features')}
              className="text-slate-300 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {t('header.features')}
            </button>
            <button 
              onClick={() => scrollToSection('why-different')}
              className="text-slate-300 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {t('header.whyDifferent')}
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className="text-slate-300 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {t('header.faq')}
            </button>
            
            {/* Desktop Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm">{getLanguageLabel()}</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${showLangMenu ? 'rotate-180' : ''}`}></i>
              </button>
              
              {showLangMenu && (
                <div className="absolute top-full right-0 mt-2 bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === 'en' ? 'text-teal-400 bg-slate-800/50' : 'text-slate-300'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('zh-TW')}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === 'zh-TW' ? 'text-teal-400 bg-slate-800/50' : 'text-slate-300'
                    }`}
                  >
                    繁體中文
                  </button>
                  <button
                    onClick={() => changeLanguage('ja')}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === 'ja' ? 'text-teal-400 bg-slate-800/50' : 'text-slate-300'
                    }`}
                  >
                    日本語
                  </button>
                </div>
              )}
            </div>

            <button 
              onClick={() => scrollToSection('signup')}
              className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-emerald-600 transition-all duration-300 shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 whitespace-nowrap cursor-pointer"
            >
              {t('header.joinEarlyAccess')}
            </button>
          </nav>

          {/* Mobile Right Section */}
          <div className="md:hidden flex items-center gap-4">
            {/* Mobile Language Button */}
            <div className="relative">
              <button
                onClick={() => setShowMobileLangMenu(!showMobileLangMenu)}
                className="text-white cursor-pointer w-8 h-8 flex items-center justify-center"
              >
                <i className="ri-global-line text-2xl"></i>
              </button>
              
              {showMobileLangMenu && (
                <div className="absolute top-full right-0 mt-2 bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === 'en' ? 'text-teal-400 bg-slate-800/50' : 'text-slate-300'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('zh-TW')}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === 'zh-TW' ? 'text-teal-400 bg-slate-800/50' : 'text-slate-300'
                    }`}
                  >
                    繁體中文
                  </button>
                  <button
                    onClick={() => changeLanguage('ja')}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === 'ja' ? 'text-teal-400 bg-slate-800/50' : 'text-slate-300'
                    }`}
                  >
                    日本語
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="text-white cursor-pointer"
              onClick={() => {
                const mobileMenu = document.getElementById('mobile-menu');
                if (mobileMenu) {
                  mobileMenu.classList.toggle('hidden');
                }
              }}
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div id="mobile-menu" className="hidden md:hidden mt-4 pb-4 space-y-4 bg-slate-950/95 backdrop-blur-lg rounded-lg px-4 py-4 border border-slate-800/50">
          <button 
            onClick={() => {
              scrollToSection('features');
              document.getElementById('mobile-menu')?.classList.add('hidden');
            }}
            className="block w-full text-left text-slate-300 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('header.features')}
          </button>
          <button 
            onClick={() => {
              scrollToSection('why-different');
              document.getElementById('mobile-menu')?.classList.add('hidden');
            }}
            className="block w-full text-left text-slate-300 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('header.whyDifferent')}
          </button>
          <button 
            onClick={() => {
              scrollToSection('faq');
              document.getElementById('mobile-menu')?.classList.add('hidden');
            }}
            className="block w-full text-left text-slate-300 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('header.faq')}
          </button>

          <button 
            onClick={() => {
              scrollToSection('signup');
              document.getElementById('mobile-menu')?.classList.add('hidden');
            }}
            className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-emerald-600 transition-all duration-300 shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 whitespace-nowrap cursor-pointer inline-block"
          >
            {t('header.joinEarlyAccess')}
          </button>
        </div>
      </div>
    </header>
  );
}
